import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { date, hhmm } from "@/lib/format";
import { JobTitle } from "@/components/JobTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, MessageSquare } from "lucide-react";

export const TIME_EDIT_ROLES = ["supervisor", "office", "management", "support"];

function EditTime({ entry, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ h: Math.floor(entry.minutes / 60), m: entry.minutes % 60, date: entry.date, note: entry.note || "", reason: "" }));
  const mins = Number(f.h || 0) * 60 + Number(f.m || 0);
  const save = async () => {
    try {
      const timesChanged = mins !== entry.minutes ? { start_time: "", finish_time: "" } : {};
      await api.patch(`/job-time/${entry.id}`, { minutes: mins, date: f.date, note: f.note, reason: f.reason, ...timesChanged });
      toast.success(`Time updated to ${hhmm(mins)}`); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const adj = (d) => { const t = Math.max(mins + d, 0); setF((s) => ({ ...s, h: Math.floor(t / 60), m: t % 60 })); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-time-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Edit Time</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{entry.user_name} · {entry.customer_name ? `${entry.customer_name} · ` : ""}{entry.job_name} · {entry.stage_name}</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Hours</Label><Input type="number" min="0" value={f.h} onChange={(e) => setF({ ...f, h: e.target.value })} data-testid="edit-time-hours" /></div>
          <div><Label className="text-xs">Minutes</Label><Input type="number" min="0" max="59" value={f.m} onChange={(e) => setF({ ...f, m: e.target.value })} data-testid="edit-time-minutes" /></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="edit-time-date" /></div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[-60, -30, -15, 15, 30, 60].map((d) => <Button key={d} type="button" size="sm" variant="outline" onClick={() => adj(d)} data-testid={`edit-time-adj-${d}`}>{d > 0 ? "+" : "−"}{Math.abs(d)}m</Button>)}
        </div>
        <div><Label className="text-xs">Employee note</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="edit-time-note" /></div>
        <div><Label className="text-xs">Reason for change</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. forgot to stop timer" data-testid="edit-time-reason" /></div>
        <DialogFooter><Button onClick={save} disabled={mins <= 0} data-testid="edit-time-save" className="font-bold uppercase">Save {hhmm(mins)}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TimeEntries({ rows, canEdit, onChanged, showJob = true, showUser = true, testid = "time-entries" }) {
  const [edit, setEdit] = useState(null);
  const del = async (t) => {
    if (!window.confirm(`Delete ${hhmm(t.minutes)} booked by ${t.user_name}? It goes to the Restore Bin.`)) return;
    try { await api.delete(`/job-time/${t.id}`); toast.success("Time entry deleted"); onChanged(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-2.5">Date</th>{showUser && <th className="text-left px-4 py-2.5">Employee</th>}{showJob && <th className="text-left px-4 py-2.5">Customer / Job</th>}
            <th className="text-left px-4 py-2.5">Task</th><th className="text-left px-4 py-2.5">Notes</th><th className="text-right px-4 py-2.5">Time</th>{canEdit && <th className="px-4 py-2.5" />}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((t) => (
            <tr key={t.id} data-testid={`${testid}-row-${t.id}`}>
              <td className="px-4 py-2.5 whitespace-nowrap">{date(t.date)}{t.start_time && <div className="text-[11px] font-mono-num text-muted-foreground">{t.start_time}–{t.finish_time}</div>}</td>
              {showUser && <td className="px-4 py-2.5 whitespace-nowrap">{t.user_name}</td>}
              {showJob && <td className="px-4 py-2.5 max-w-[220px]"><JobTitle j={t} /></td>}
              <td className="px-4 py-2.5">{t.stage_name || "—"}</td>
              <td className="px-4 py-2.5 max-w-[280px]" data-testid={`${testid}-note-${t.id}`}>{t.note && t.note !== "Timer" ? <span className="flex gap-1.5"><MessageSquare className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" /><span className="whitespace-pre-wrap">{t.note}</span></span> : <span className="text-muted-foreground">—</span>}</td>
              <td className="px-4 py-2.5 text-right font-mono-num whitespace-nowrap">{hhmm(t.minutes)}{t.corrected && <div className="text-[10px] text-amber-400" title={`by ${t.corrected_by}`}>✎ corrected</div>}</td>
              {canEdit && <td className="px-4 py-2.5 text-right whitespace-nowrap">
                <button onClick={() => setEdit(t)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid={`${testid}-edit-${t.id}`} title="Edit time"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(t)} className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors" data-testid={`${testid}-delete-${t.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {edit && <EditTime entry={edit} onClose={() => setEdit(null)} onSaved={onChanged} />}
    </div>
  );
}
